import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { fileUrl } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("ms-MY", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
}

const STAMP_OVERLAYS = {
  EXPIRED: "TAMAT TEMPOH",
  SUSPENDED: "DIGANTUNG",
  REVOKED: "KEAHLIAN DIBATALKAN",
};

const STATUS_LABELS = {
  ACTIVE: "AKTIF",
  EXPIRING_SOON: "AKAN TAMAT",
  EXPIRED: "TAMAT TEMPOH",
  SUSPENDED: "DIGANTUNG",
  REVOKED: "DIBATALKAN",
};

export function MembershipCard({ member, profile, category, compact = false }) {
  const verifyUrl = `${window.location.origin}/semak/${member?.verification_token || ""}`;
  const effectiveStatus = member?.effective_status || member?.status;
  const isActive = effectiveStatus === "ACTIVE";
  const isExpiringSoon = effectiveStatus === "EXPIRING_SOON";
  const stampLabel = STAMP_OVERLAYS[effectiveStatus];
  const photoFileId = profile?.passport_photo_file_id || member?.profile_photo_file_id;
  return (
    <div
      data-testid="digital-membership-card"
      data-status={effectiveStatus}
      className={`relative overflow-hidden rounded-2xl bg-piks-black text-white shadow-2xl border border-piks-goldline/30 transition-all duration-300 ${compact ? "w-full max-w-sm" : "w-full max-w-md"} ${!isActive && !isExpiringSoon ? "grayscale contrast-90 brightness-90" : ""}`}
      style={{ aspectRatio: "1.586/1" }}
    >
      <div className="absolute inset-0">
        <img src="/card/kad-background.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/78" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/70" />
      </div>
      <div className="absolute inset-0 pattern-dots opacity-25" />
      <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-piks-gold/10 blur-2xl" />

      {isExpiringSoon && (
        <div
          data-testid="card-expiring-soon-banner"
          className="absolute top-0 left-0 right-0 z-10 bg-amber-500 text-black text-[10px] font-bold tracking-wider text-center py-1 flex items-center justify-center gap-1.5"
        >
          <AlertTriangle className="w-3 h-3" /> AKAN TAMAT TEMPOH — SAHKAN PEMBAHARUAN
        </div>
      )}

      {stampLabel && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none" data-testid="card-status-stamp">
          <div className="bg-piks-red/90 border-2 border-white/80 text-white font-display font-bold tracking-[0.15em] text-center py-1.5 px-6 -rotate-[18deg] shadow-lg" style={{ fontSize: compact ? "13px" : "16px" }}>
            {stampLabel}
          </div>
        </div>
      )}

      <div className="relative h-full flex flex-col justify-between p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/piks-logo.png?v=7" alt="PPPS" className="h-9 w-9 object-contain" />
            <div>
              <p className="font-display text-[13px] tracking-[0.15em] text-white">AHLI PPPS</p>
              <p className="text-[9px] text-piks-gold/80 tracking-wider">PERSATUAN PENGAWAL PERIBADI SARAWAK</p>
            </div>
          </div>
          <ShieldCheck className="w-5 h-5 text-piks-gold" />
        </div>

        <div className="flex items-end justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-lg bg-white/10 border border-white/20 overflow-hidden flex items-center justify-center shrink-0">
              {photoFileId ? (
                <img src={fileUrl(photoFileId)} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-lg font-display text-white/50">{profile?.full_name?.[0] || "A"}</span>
              )}
            </div>
            <div>
              <p className="font-display text-base tracking-wide text-white" data-testid="card-member-name">{profile?.full_name || "-"}</p>
              <p className="text-[11px] font-mono text-piks-gold tracking-wider" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.9)" }} data-testid="card-member-number">{member?.member_number}</p>
              <p className="text-[10px] text-white/60 uppercase tracking-wide mt-0.5">{category?.name || "-"}</p>
            </div>
          </div>
          <div className="bg-white p-1.5 rounded-md shrink-0">
            <QRCodeSVG value={verifyUrl} size={64} data-testid="card-qr-code" />
          </div>
        </div>

        <div className="flex items-center justify-between text-[10px] text-white/50 pt-2 border-t border-white/10">
          <span>SAH SEHINGGA <span className="text-white font-medium">{formatDate(member?.valid_until)}</span></span>
          <span
            data-testid="card-status-label"
            className={`font-semibold tracking-wide ${isActive ? "text-emerald-400" : isExpiringSoon ? "text-amber-400" : "text-piks-red"}`}
          >
            {STATUS_LABELS[effectiveStatus] || effectiveStatus}
          </span>
        </div>
      </div>
    </div>
  );
}
